import { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { adminApi } from '@/lib/api';
import { DAY_NAMES } from '@/lib/hours';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { Plus, Trash2, CalendarIcon, Save, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useAuth } from '@/lib/auth';

const defaultHours = DAY_NAMES.map((name, i) => ({
  day: i,
  day_name: name,
  is_closed: i === 0 || i === 6,
  slots: (i === 0 || i === 6) ? [] : [{ start: '11:30', end: '14:30' }, { start: '19:00', end: '22:30' }],
}));

export default function HoursManagement() {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('HOURS', 'edit');
  const [hours, setHours] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showExcDialog, setShowExcDialog] = useState(false);
  const [excForm, setExcForm] = useState({ date: null, label: '', is_closed: true, slots: [] });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.getHours();
      const data = res.data;
      const apiHours = data.hours || [];
      const apiExceptions = data.exceptions || [];

      if (apiHours.length > 0) {
        setHours(apiHours.map(h => ({
          ...h,
          day_name: DAY_NAMES[h.day] || `Jour ${h.day}`,
          slots: (h.slots || []).map(s => ({ start: s.start || '', end: s.end || '' })),
        })));
      } else {
        setHours(defaultHours);
      }
      setExceptions(apiExceptions);
    } catch (err) {
      setError(err.response?.data?.detail || 'Impossible de charger les horaires');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const updateDay = (dayIndex, field, value) => {
    setHours(prev => prev.map(h => h.day === dayIndex ? { ...h, [field]: value } : h));
  };

  const updateSlot = (dayIndex, slotIndex, field, value) => {
    setHours(prev => prev.map(h => {
      if (h.day !== dayIndex) return h;
      const newSlots = [...h.slots];
      newSlots[slotIndex] = { ...newSlots[slotIndex], [field]: value };
      return { ...h, slots: newSlots };
    }));
  };

  const addSlot = (dayIndex) => {
    setHours(prev => prev.map(h => h.day === dayIndex ? { ...h, slots: [...h.slots, { start: '19:00', end: '22:30' }] } : h));
  };

  const removeSlot = (dayIndex, slotIndex) => {
    setHours(prev => prev.map(h => h.day === dayIndex ? { ...h, slots: h.slots.filter((_, i) => i !== slotIndex) } : h));
  };

  const saveHours = async () => {
    // Validate: open days must have at least one slot with start & end
    const invalidDays = hours.filter(h => !h.is_closed && (!h.slots || h.slots.length === 0 || h.slots.some(s => !s.start || !s.end)));
    if (invalidDays.length > 0) {
      const names = invalidDays.map(h => h.day_name).join(', ');
      toast.error(`Veuillez renseigner au moins un créneau complet pour : ${names}`);
      return;
    }
    setSaving(true);
    try {
      await adminApi.updateHours({
        hours: hours.map(h => ({
          day: h.day,
          is_closed: h.is_closed,
          slots: h.is_closed ? [] : h.slots.map(s => ({ start: s.start, end: s.end })),
        }))
      });
      toast.success('Horaires sauvegardés');
    } catch { toast.error('Erreur lors de la sauvegarde'); }
    setSaving(false);
  };

  const saveException = async () => {
    if (!excForm.date) return;
    try {
      await adminApi.createException({
        date: format(excForm.date, 'yyyy-MM-dd'),
        label: excForm.label,
        is_closed: excForm.is_closed,
        slots: excForm.is_closed ? [] : excForm.slots,
      });
      setShowExcDialog(false);
      load();
      toast.success('Exception ajoutée');
    } catch { toast.error('Erreur'); }
  };

  const deleteException = async (id) => {
    try {
      await adminApi.deleteException(id);
      load();
      toast.success('Exception supprimée');
    } catch { toast.error('Erreur'); }
  };

  if (loading) return (
    <AdminLayout title="Gestion des Horaires">
      <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-garden-800 border-t-transparent rounded-full animate-spin" /></div>
    </AdminLayout>
  );

  if (error) return (
    <AdminLayout title="Gestion des Horaires">
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center" data-testid="hours-error">
        <AlertCircle className="mx-auto text-red-500 mb-2" size={24} />
        <p className="text-sm text-red-700 font-medium">{error}</p>
        <Button onClick={load} className="mt-4 bg-garden-800 hover:bg-garden-900 text-white rounded-full text-sm">Réessayer</Button>
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout title="Gestion des Horaires">
      <div data-testid="admin-hours-management" className="space-y-8">
        {/* Weekly Hours */}
        <div className="bg-white rounded-xl border border-garden-200 p-6">
          <h2 className="font-heading font-semibold text-lg text-garden-800 mb-4">Horaires hebdomadaires</h2>
          <div className="space-y-4">
            {hours.map((h) => (
              <div key={h.day} className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pb-4 border-b border-garden-100 last:border-0" data-testid={`admin-hours-day-${h.day}`}>
                <div className="w-28 shrink-0">
                  <span className="font-medium text-charcoal">{h.day_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={!h.is_closed} onCheckedChange={(v) => updateDay(h.day, 'is_closed', !v)} data-testid={`hours-toggle-${h.day}`} disabled={!canEdit} />
                  <span className="text-xs text-charcoal/50">{h.is_closed ? 'Fermé' : 'Ouvert'}</span>
                </div>
                {!h.is_closed && (
                  <div className="flex flex-wrap gap-2 items-center">
                    {h.slots?.map((slot, si) => (
                      <div key={si} className="flex items-center gap-1 bg-garden-50 rounded-lg px-2 py-1">
                        <Input type="time" value={slot.start} onChange={e => updateSlot(h.day, si, 'start', e.target.value)} className="w-28 h-8 text-xs border-garden-200 rounded-lg" data-testid={`hours-slot-start-${h.day}-${si}`} disabled={!canEdit} />
                        <span className="text-xs text-charcoal/50">—</span>
                        <Input type="time" value={slot.end} onChange={e => updateSlot(h.day, si, 'end', e.target.value)} className="w-28 h-8 text-xs border-garden-200 rounded-lg" data-testid={`hours-slot-end-${h.day}-${si}`} disabled={!canEdit} />
                        {canEdit && <button onClick={() => removeSlot(h.day, si)} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={12} /></button>}
                      </div>
                    ))}
                    {canEdit && <button onClick={() => addSlot(h.day)} className="text-xs text-garden-600 hover:text-garden-800 px-2 py-1" data-testid={`hours-add-slot-${h.day}`}>+ Créneau</button>}
                  </div>
                )}
              </div>
            ))}
          </div>
          {canEdit && (
          <Button onClick={saveHours} disabled={saving} data-testid="admin-save-hours-btn" className="bg-garden-800 hover:bg-garden-900 text-white rounded-full mt-6">
            <Save size={16} className="mr-1" /> {saving ? 'Sauvegarde...' : 'Sauvegarder les horaires'}
          </Button>
          )}
        </div>

        {/* Exceptions */}
        <div className="bg-white rounded-xl border border-garden-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="font-heading font-semibold text-lg text-garden-800">Horaires exceptionnels</h2>
            {canEdit && (
            <Button onClick={() => { setExcForm({ date: null, label: '', is_closed: true, slots: [] }); setShowExcDialog(true); }} data-testid="admin-add-exception-btn" className="bg-garden-800 hover:bg-garden-900 text-white rounded-full text-sm">
              <Plus size={14} className="mr-1" /> Ajouter
            </Button>
            )}
          </div>
          {exceptions.length > 0 ? (
            <div className="space-y-2">
              {exceptions.map((exc) => (
                <div key={exc.id} className="flex justify-between items-center bg-garden-50 rounded-lg px-4 py-3" data-testid={`admin-exception-${exc.id}`}>
                  <div>
                    <span className="font-medium text-charcoal text-sm">{exc.date}</span>
                    {exc.label && <span className="text-xs text-charcoal/50 ml-2">({exc.label})</span>}
                    <span className={`ml-2 text-xs ${exc.is_closed ? 'text-red-500' : 'text-green-600'}`}>
                      {exc.is_closed ? 'Fermé' : (exc.slots || []).map(s => `${s.start}–${s.end}`).join(' / ')}
                    </span>
                  </div>
                  <button onClick={() => deleteException(exc.id)} className="text-red-400 hover:text-red-600" disabled={!canEdit}><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-charcoal/50">Aucune exception définie</p>
          )}
        </div>

        {/* Exception Dialog */}
        <Dialog open={showExcDialog} onOpenChange={setShowExcDialog}>
          <DialogContent className="bg-white max-w-md">
            <DialogHeader><DialogTitle className="text-garden-800">Ajouter une exception</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-sm">Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start border-garden-200 rounded-xl" data-testid="exception-date-picker">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {excForm.date ? format(excForm.date, 'dd/MM/yyyy') : 'Choisir une date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white"><Calendar mode="single" selected={excForm.date} onSelect={(d) => setExcForm({...excForm, date: d})} locale={fr} /></PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-sm">Libellé</Label>
                <Input value={excForm.label} onChange={e => setExcForm({...excForm, label: e.target.value})} placeholder="Ex: Jour férié" className="border-garden-200 rounded-xl" data-testid="exception-label" />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={excForm.is_closed} onCheckedChange={v => setExcForm({...excForm, is_closed: v, slots: v ? [] : [{ start: '11:30', end: '14:30' }]})} data-testid="exception-closed-toggle" />
                <Label className="text-sm">{excForm.is_closed ? 'Fermé ce jour' : 'Ouvert ce jour'}</Label>
              </div>
              {!excForm.is_closed && (
                <div className="space-y-2">
                  <Label className="text-sm">Créneaux d'ouverture</Label>
                  {(excForm.slots || []).map((slot, si) => (
                    <div key={si} className="flex items-center gap-2">
                      <Input type="time" value={slot.start} onChange={e => { const ns = [...excForm.slots]; ns[si] = {...ns[si], start: e.target.value}; setExcForm({...excForm, slots: ns}); }} className="w-28 h-8 text-xs border-garden-200 rounded-lg" />
                      <span className="text-xs text-charcoal/50">—</span>
                      <Input type="time" value={slot.end} onChange={e => { const ns = [...excForm.slots]; ns[si] = {...ns[si], end: e.target.value}; setExcForm({...excForm, slots: ns}); }} className="w-28 h-8 text-xs border-garden-200 rounded-lg" />
                      <button onClick={() => setExcForm({...excForm, slots: excForm.slots.filter((_, i) => i !== si)})} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={12} /></button>
                    </div>
                  ))}
                  <button onClick={() => setExcForm({...excForm, slots: [...(excForm.slots || []), { start: '19:00', end: '22:30' }]})} className="text-xs text-garden-600 hover:text-garden-800">+ Ajouter un créneau</button>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowExcDialog(false)} className="rounded-full">Annuler</Button>
              <Button onClick={saveException} className="bg-garden-800 hover:bg-garden-900 text-white rounded-full" data-testid="exception-save-btn">Ajouter</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
